import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import { message } from 'antd'
import requests from '../../../axios/requests'
import s from './gen.module.scss'
import p from './plans.module.scss'
import { ChipsBox } from './GenFrame'

const MAX_SIZE = 10 * 1024 * 1024
const ALLOWED = ['pdf', 'docx', 'pptx', 'xlsx']

export const docName = (doc) => doc?.file_name || doc?.name || doc?.title || 'Документ'
const extOf = (doc) => (docName(doc).split('.').pop() || 'PDF').slice(0, 4).toUpperCase()

// Документы-источники: загруженные файлы + загрузка нового (PDF, DOCX, PPTX, XLSX до 10 МБ).
// bare — только список, для верхней зоны генератора вместо навыков учебного плана.
const DocSource = ({ selected, onSelect, language = 'ru', bare }) => {
  const [docs, setDocs] = useState([])
  const [uploading, setUploading] = useState(false)

  const fetchDocs = () =>
    requests.users
      .get_speciality_pdf_list()
      .then((res) => {
        const raw = res.data?.results ?? res.data ?? []
        const list = Array.isArray(raw) ? raw : []
        setDocs(list)
        return list
      })
      .catch(() => [])

  useEffect(() => {
    fetchDocs()
  }, [])

  const upload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!ALLOWED.includes(ext)) return message.error('Поддерживаются форматы: PDF, DOCX, PPTX, XLSX')
    if (file.size > MAX_SIZE) return message.error('Размер файла не должен превышать 10 МБ')
    setUploading(true)
    try {
      const res = await requests.users.upload_speciality_pdf(file, language)
      message.success('Документ загружен')
      const list = await fetchDocs()
      const created = (res.data?.id && list.find((d) => d.id === res.data.id)) || res.data
      if (created?.id) onSelect(created)
    } catch (err) {
      message.error(err.response?.data?.error || err.response?.data?.message || 'Ошибка при загрузке документа')
    } finally {
      setUploading(false)
    }
  }

  const chips = docs.map((doc) => {
    const on = selected?.id === doc.id
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        key={doc.id}
        title={docName(doc)}
        className={cn(s.pchip, s.pchipBtn, { [s.pchipOn]: on })}
        style={{ maxWidth: 340, minWidth: 0 }}
        onClick={() => onSelect(doc)}
      >
        {/* как навыки учебного плана: «таблетка», формат файла - подписью, как статус у навыка */}
        <span className={p.docName}>{docName(doc).replace(/\.[a-z0-9]+$/i, '')}</span>
        <span className={cn(s.muted, s.small)} style={{ fontWeight: 600, flex: 'none' }}>
          {extOf(doc)}
        </span>
      </button>
    )
  })
  const uploadBtn = (
    <label className={cn(s.btn, s.ghost, p.upload)} aria-disabled={uploading || undefined}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        aria-hidden="true"
      >
        <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
      </svg>
      {uploading ? 'Загружаем…' : docs.length ? 'Загрузить другой' : 'Загрузить документ'}
      <input
        type="file"
        accept=".pdf,.docx,.pptx,.xlsx"
        aria-label="Загрузить документ"
        disabled={uploading}
        onChange={upload}
      />
    </label>
  )
  const hint = <span className={cn(s.muted, s.small)}>PDF, DOCX, PPTX, XLSX · до 10 МБ</span>

  // в верхней зоне — как навыки учебного плана: два ряда, «Показать все», действия в третьей строке
  if (bare)
    return (
      <ChipsBox
        count={docs.length}
        role="radiogroup"
        aria-label="Документ"
        footer={
          <>
            {hint}
            {uploadBtn}
          </>
        }
      >
        {chips}
      </ChipsBox>
    )

  const list = (
    <div className={p.docs} role="radiogroup" aria-label="Документ">
      {chips}
      {uploadBtn}
      {hint}
    </div>
  )

  return (
    <section className={p.docbar} aria-label="Документ-источник">
      <div className={p.docbarHead}>
        <h3>Документ-источник</h3>
        <span className={cn(s.muted, s.small)}>ИИ возьмёт структуру и темы из файла</span>
      </div>
      {list}
    </section>
  )
}

export default DocSource
